import { PermissionEnum } from '@api/enum';
import { ClientInstallationStatusEnum } from '@api/enum/client-installation.enum';
import { useClientInstallationStatusCount } from '@hooks/useClientInstallationStatusCount';
import { useSiteRecceStatusCount } from '@hooks/useSiteRecceStatusCount';
import { Sidebar } from '@hyperflake/react-ui-library';
import { ProgressTabProvider } from '@library/progress-tab';
import { TRootState } from '@store/index';
import { FeatureRolloutProvider } from 'modules/shared/features/feature-rollout';
import useAuthPermission from 'modules/shared/hooks/use-auth-permission';
import { useSupportCounterData } from 'modules/shared/hooks/use-badge-count';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';

interface NotificationBadgeProps {
    count: number;
}

const NotificationBadge = (props: NotificationBadgeProps) => {
    const { count } = props;

    return (
        <div className="text-[10px] bg-warning w-5 h-5 ml-[0.25rem] -mt-[0.8rem] rounded-full grid place-items-center text-warning-contrast font-semibold">
            {count}
        </div>
    );
};

const AppSidebar = () => {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { supportCounter, internalSupportCounter, publicApiCounter } = useSupportCounterData();
    const { hasPermission } = useAuthPermission();
    const { profile } = useSelector((state: TRootState) => state);
    const { siteRecceCounter } = useSiteRecceStatusCount();
    const { clientInstallationCounter } = useClientInstallationStatusCount(ClientInstallationStatusEnum.PENDING);

    // const isInternalPathActive =
    //     pathname.startsWith('/internal-support') ||
    //     pathname.startsWith('/internal-applications') ||
    //     pathname.startsWith('/analytics/sign-in-logs');
    const [isInternalOpen, setIsInternalOpen] = useState(true);

    return (
        <FeatureRolloutProvider>
            {({ hasFeature }) => {
                const navItems = [
                    {
                        content: 'Clients',
                        href: '/clients',
                        isActive: pathname.startsWith('/clients'),
                        permission: hasPermission(PermissionEnum.CLIENT_READ),
                    },
                    {
                        content: 'All Timelapse Cameras',
                        href: '/cameras',
                        isActive: pathname.startsWith('/cameras'),
                        permission: hasPermission(PermissionEnum.RESELLER_CAMERA_READ),
                    },
                    {
                        content: 'All Live Cameras',
                        href: '/live-cameras',
                        isActive: pathname.startsWith('/live-cameras'),
                        permission: hasPermission(PermissionEnum.RESELLER_CAMERA_READ),
                    },
                    {
                        content: 'All Users',
                        href: '/all-users',
                        isActive: pathname.startsWith('/all-users'),
                        permission: hasPermission(PermissionEnum.ALL_USERS_READ),
                    },
                    {
                        content: (
                            <div className="relative flex items-center">
                                <div>Support</div>
                                {supportCounter?.count !== 0 && <NotificationBadge count={supportCounter?.count} />}
                            </div>
                        ),
                        href: '/support-tickets',
                        isActive: pathname.startsWith('/support-tickets'),
                        permission: hasPermission(PermissionEnum.SUPPORT_READ),
                    },
                    {
                        content: (
                            <div className="relative flex items-center">
                                <div>Public API Request</div>
                                {publicApiCounter?.count !== 0 && <NotificationBadge count={publicApiCounter?.count} />}
                            </div>
                        ),
                        href: '/public-requests',
                        isActive: pathname.startsWith('/public-requests'),
                        permission: hasPermission(PermissionEnum.PUBLIC_API_REQUEST_READ),
                    },

                    {
                        // content: 'Installation',
                        content: (
                            <div className="relative flex items-center">
                                <div>Installation</div>

                                {clientInstallationCounter?.PENDING !== 0 && (
                                    <NotificationBadge count={clientInstallationCounter?.PENDING} />
                                )}
                            </div>
                        ),
                        href: '/client-installations',
                        isActive: pathname.startsWith('/client-installations'),
                        permission:
                            // profile.info.email === 'faizan@code.in' &&
                            hasFeature('CLIENT_INSTALLATION'),
                    },
                    {
                        // content: 'All Site Recce',

                        content: (
                            <div className="relative flex items-center">
                                <div>All Site Recce</div>
                                {siteRecceCounter?.UNASSIGNED !== 0 && (
                                    <NotificationBadge count={siteRecceCounter?.UNASSIGNED} />
                                )}
                            </div>
                        ),
                        href: '/all-site-recce',
                        isActive: pathname.startsWith('/all-site-recce'),
                        permission:
                            // profile.info.email === 'faizan@code.in' &&
                            hasFeature('ALL_SITE_RECEE'),
                    },
                    {
                        content: 'GridWall Request',
                        href: '/all-grid-wall-requests',
                        isActive: pathname.startsWith('/all-grid-wall-requests'),
                        permission: hasPermission(PermissionEnum.GRIDWALL_REQUEST_READ),
                    },
                    {
                        content: 'Box Storage',
                        href: '/box-storage',
                        isActive: pathname.startsWith('/box-storage'),
                        permission: hasPermission(PermissionEnum.BOX_STROAGE_READ),
                    },
                    {
                        content: 'Push Notification',
                        href: '/push-notification',
                        isActive: pathname.startsWith('/push-notification'),
                        permission: hasPermission(PermissionEnum.NOTIFICATION_READ),
                    },
                    {
                        content: 'Announcement',
                        href: '/announcement',
                        isActive: pathname.startsWith('/announcement'),
                        permission: hasPermission(PermissionEnum.ANNOUNCEMENT_READ),
                    },
                    {
                        content: 'Internal',
                        isActive: false,
                        permission: true,
                        children: [
                            {
                                content: (
                                    <div className="relative flex gap-[2px] items-center">
                                        <div>Internal Support</div>
                                        {internalSupportCounter?.count !== 0 && (
                                            <NotificationBadge count={internalSupportCounter?.count} />
                                        )}
                                        <div className="text-[8px]">Beta</div>
                                    </div>
                                ),
                                href: '/internal-support',
                                isActive: pathname.startsWith('/internal-support'),
                                permission: hasPermission(PermissionEnum.SUPPORT_READ),
                            },
                            {
                                content: 'Internal Applications',
                                href: '/internal-applications',
                                isActive: pathname.startsWith('/internal-applications'),
                                permission: hasPermission(PermissionEnum.INTERNAL_APPLICATION_READ),
                            },
                            {
                                content: 'Analytics',
                                href: '/analytics/sign-in-logs',
                                isActive: pathname.startsWith('/analytics/sign-in-logs'),
                                permission: hasPermission(PermissionEnum.SIGN_IN_LOGS_READ),
                            },
                        ],
                    },
                ];

                return (
                    <ProgressTabProvider>
                        <Sidebar className="border-r-0 md:border-r ">
                            <Sidebar.Nav>
                                {navItems.map(
                                    (row, i) =>
                                        row.permission &&
                                        (row.children ? (
                                            <div key={i}>
                                                <Sidebar.NavItem
                                                    isActive={row.isActive}
                                                    onClick={() => setIsInternalOpen(!isInternalOpen)}
                                                    className="cursor-pointer flex items-center justify-between"
                                                >
                                                    <span>{row.content}</span>
                                                    <span
                                                        className="text-xs transition-transform duration-200"
                                                        style={{
                                                            transform: isInternalOpen
                                                                ? 'rotate(90deg)'
                                                                : 'rotate(0deg)',
                                                        }}
                                                    >
                                                        <svg
                                                            width="20px"
                                                            height="20px"
                                                            viewBox="0 0 25 25"
                                                            fill="none"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <path
                                                                d="M10.5 8L15 12.5L10.5 17"
                                                                stroke="#121923"
                                                                stroke-width="1.5"
                                                            />
                                                        </svg>
                                                    </span>
                                                </Sidebar.NavItem>
                                                {isInternalOpen && (
                                                    <div className="pl-4 space-y-1">
                                                        {row.children.map(
                                                            (child, j) =>
                                                                child.permission && (
                                                                    <Sidebar.NavItem
                                                                        key={`${i}-${j}`}
                                                                        isActive={child.isActive}
                                                                        onClick={() => navigate(child.href)}
                                                                    >
                                                                        <Link to={child.href}>{child.content}</Link>
                                                                    </Sidebar.NavItem>
                                                                )
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <Sidebar.NavItem
                                                key={i}
                                                isActive={row.isActive}
                                                onClick={() => navigate(row.href)}
                                            >
                                                <Link to={row.href}>{row.content}</Link>
                                            </Sidebar.NavItem>
                                        ))
                                )}
                            </Sidebar.Nav>
                        </Sidebar>
                    </ProgressTabProvider>
                );
            }}
        </FeatureRolloutProvider>
    );
};

export default AppSidebar;
